import { useParams } from "react-router-dom";
import React, { useState, useEffect, useCallback } from "react";
import CreateIDEProjectModal from "./create-ide-project-modal";
import FileManager from "../utils/file-manager";
import { ReactComponent as ExpandIcon } from '../../assets/logos/expand.svg';
import { ReactComponent as CollapseIcon } from '../../assets/logos/collapse.svg';
import { ReactComponent as BulbIcon } from '../../assets/logos/bulb.svg';
import {
  Button,
  Space,
  message,
  Skeleton,
  Modal,
  Tooltip,
  Badge,
} from "antd";
import {
  //   FullscreenOutlined,
  //   FullscreenExitOutlined,
} from "@ant-design/icons";
import {
  getFileExtension,
  trackEvent,
  getCssClassNameForAutoSize,
  isMobile,
} from "../utils/util-functions";
import { global_var } from "../../services/cs-lab-config";
import {
  getIdeProjectDetails,
  getFileContent,
  saveFileInIde,
} from "../../services/ide-apis";
import IdeHeader from "./ide-header";
import Editor from "@monaco-editor/react";
import { ReactComponent as OptionIcon } from '../../assets/logos/options.svg';

const HtmlCssIDE = () => {
  document.title = "CS Lab | Html CSS IDE";

  const [selectedTheme, setSelectedTheme] = useState("light");
  const [newProjectModal, setNewProjectModal] = useState(false);

  const [ideProjectDetails, setIdeProjectDetails] = useState(undefined);
  const [selectedFile, setSelectedFile] = useState(undefined);
  const [inputCode, setInputCode] = useState("");

  const [fileLoadingInProgress, setFileLoadingInProgress] = useState(false);
  const [fileSavingInProgress, setFileSavingInProgress] = useState(false);

  const [unsavedChangePresent, setUnsavedChangePresent] = useState(false);

  const [outputWindowExpanded, setOutputWindowExpanded] = useState(false);
  const [fileManagerExpanded, setFileManagerExpanded] = useState(true);
  const [mobileFileManagerModal, setMobileFileManagerModal] = useState(false);

  // const [submittedForEvaluation, setSubmittedForEvaluation] = useState(false);
  // const [evaluationStatus, setEvaluationStatus] = useState(undefined);
  const [readOnlyMode, setReadOnlyMode] = useState(false);

  let { ideProjectId } = useParams();

  let hideHeader = new URLSearchParams(window.location.search).get(
    "hideHeader"
  );

  const navigateToFile = useCallback(
    (event) => {
      if (!ideProjectId) {
        return;
      }

      if (event.node.fileMimeType === "image" || event.node.fileMimeType === "unknown") {
        setOutputWindowExpanded(false);
        setSelectedFile(event.node);
        setUnsavedChangePresent(false);
      } else {
        setSelectedFile(undefined);
        setFileLoadingInProgress(true);

        getFileContent(ideProjectId, event.node.id).then((response) => {
          setFileLoadingInProgress(false);

          response = response.data;

          if (response.statusCode === 200) {
            event.node.fileContent = response.fileContent;
            setInputCode(event.node.fileContent);
            setSelectedFile(event.node);
            setUnsavedChangePresent(false);
          } else {
            message.error(response.message);
          }
        });
      }
    },
    [ideProjectId]
  );

  function getCssClassName(component) {
    return getCssClassNameForAutoSize(
      component,
      fileManagerExpanded,
      outputWindowExpanded
    );
  }

  async function saveFileInIdeHandler() {
    setFileSavingInProgress(true);

    saveFileInIde(ideProjectId, selectedFile.id, {
      fileContent: inputCode,
      updateS3Content: true,
      fileOrFolderObject: {},
    }).then((response) => {
      setFileSavingInProgress(false);
      response = response.data;

      if (response.statusCode === 200) {
        message.success(response.message);
        setUnsavedChangePresent(false);
      } else {
        message.error(response.message);
      }
    });
  }

  function handleSaveFile() {
    // The following function will handle file saving

    if (!unsavedChangePresent) {
      message.info("There no changes to save");
      return;
    }

    trackEvent("ide_save_button_clicked", {
      ide_project_stack_name: "html_css",
    });

    saveFileInIdeHandler();
  }

  const nodeSelected = useCallback(
    (nodeIds, event) => {
      // The following function will handle file opening

      if (!event.node.isFile) {
        return;
      }

      if (unsavedChangePresent) {
        Modal.confirm({
          title: "There are unsaved changes in your editor",
          cancelText: "Leave editor",
          okText: "Back to editor",
          onCancel() {
            navigateToFile(event);
          },
        });
        return;
      }

      navigateToFile(event);
    },
    [unsavedChangePresent, navigateToFile]
  );

  const handleNewFile = useCallback(
    (fileMetaListObj) => {
      if (!fileMetaListObj.isFile) {
        return;
      }

      if (!unsavedChangePresent) {
        navigateToFile({ node: fileMetaListObj });
      }
    },
    [navigateToFile, unsavedChangePresent]
  );

  const handleFileDeleted = useCallback(
    (deletedNode) => {
      if (deletedNode.id === (selectedFile ? selectedFile.id : false)) {
        setSelectedFile(undefined);
      }
    },
    [selectedFile, setSelectedFile]
  );

  const handleFileRenamed = useCallback(
    (newTreeData, newFileMetadata) => {
      if (newFileMetadata.id === (selectedFile ? selectedFile.id : false)) {
        setSelectedFile((oldFileObj) => {
          const newObj = {
            ...oldFileObj,
            fileName: newFileMetadata.fileMetaListObj.fileName,
            fileMimeType: newFileMetadata.fileMetaListObj.fileMimeType,
          };
          if (newFileMetadata.fileMetaListObj.fileS3reference) {
            newObj.fileS3reference =
              newFileMetadata.fileMetaListObj.fileS3reference;
          }
          return newObj;
        });
      }
    },
    [selectedFile, setSelectedFile]
  );

  const handleSelectedTheme = () => {
    const themeName = selectedTheme === 'light' ? 'vs-dark' : 'light';

    trackEvent("ide_dark_mode_toggle_clicked", {
      ide_project_stack_name: "html_css",
      ide_theme_value: themeName,
    });

    setSelectedTheme(themeName);
  }

  function handleCodeChange(inputCode) {
    setInputCode(inputCode);
    setUnsavedChangePresent(true);
  }

  async function handleOpen() {
    const fileExt = getFileExtension(selectedFile.fileName);

    if (fileExt !== "html") {
      message.error("You can only open .html files");
      return;
    }

    if (unsavedChangePresent) {
      trackEvent("ide_run_clicked_without_save", {
        ide_project_stack_name: "html_css",
      });
      await saveFileInIdeHandler();
    }

    trackEvent("ide_run_button_clicked", {
      ide_project_stack_name: "html_css",
    });
    
    const cacheBuster = new Date().getTime();
    window.open(selectedFile.fileS3reference + '?v='+ cacheBuster, "_blank");
  }

  //   function toggleOutputWindow() {
  //     setOutputWindowExpanded(!outputWindowExpanded);
  //   }

  //   function handleRunCode() {}

  useEffect(() => {
    if (!ideProjectDetails) {
      return;
    }

    const findNode = (tData, fileId, dataObj) => {
      for (let i = 0; i < tData.length; i++) {
        const node = tData[i];
        if (node.id === fileId) {
          dataObj["node"] = node;
          return true;
        }
        if (node.children) {
          if (findNode(node.children, fileId, dataObj)) {
            return true;
          }
        }
      }
      return false;
    };

    if (ideProjectDetails.lastAccessedFileId) {
      const dataObj = {};

      findNode(
        ideProjectDetails.fileMetaList,
        ideProjectDetails.lastAccessedFileId,
        dataObj
      );

      if (dataObj.node) {
        navigateToFile({ node: dataObj.node });
      }
    }
  }, [ideProjectDetails, navigateToFile]);

  useEffect(() => {
    if (ideProjectId) {
      setNewProjectModal(false);
      getIdeProjectDetails(ideProjectId).then((response) => {
        response = response.data;

        if (response.statusCode === 200) {
          if (
            response.projectDetails.fkUserLoginId ===
              global_var.user_login_id ||
            global_var.role_id !== global_var.STUDENT_ROLE_ID
          ) {
            if (
              response.projectDetails.evaluationStatus === "Evaluated" ||
              response.projectDetails.fkUserLoginId !== global_var.user_login_id
            ) {
              setReadOnlyMode(true);
            }
            setIdeProjectDetails(response.projectDetails);
            // setSubmittedForEvaluation(
            //   response.projectDetails.submittedForEvaluation
            // );
            // setEvaluationStatus(response.projectDetails.evaluationStatus);
          } else {
            message.error("you are not authorised");
          }
        } else {
          message.error(response.message);
        }
      });
    } else {
      setIdeProjectDetails(undefined);
      setSelectedFile(undefined);
      setNewProjectModal(true);
    }
  }, [ideProjectId]);

  const openMobileFileManagerModal = () => {
    setMobileFileManagerModal(true);
  };

  const closeMobileFileManagerModal = () => {
    setMobileFileManagerModal(false);
  };

  return (
    <div className="container-fluid p-0">
      <div className="row mt-1">
        <div className="col">
          {ideProjectDetails && hideHeader !== "true" && (
            <IdeHeader
              headerName="CS Html/Css IDE"
              showInPortFolio={ideProjectDetails.showInPortFolio}
              submittedForEvaluation={ideProjectDetails.submittedForEvaluation}
              // setSubmittedForEvaluation={setSubmittedForEvaluation}
              ideProjectDetails={ideProjectDetails}
              unsavedChangePresent={unsavedChangePresent}
              readOnlyMode={readOnlyMode}
              fkResourceId={ideProjectDetails.fkResourceId}
            />
          )}


          <div className="row mt-2 rounded-top-left pt-2 bg-white">
            {ideProjectDetails ? (
              <div
                className={getCssClassName("file-manager")}
                style={{ height: "85vh" }}
              >
                <FileManager
                  fileStructure={
                    ideProjectDetails.fileMetaList
                      ? ideProjectDetails.fileMetaList
                      : []
                  }
                  defaultSelectedKey={ideProjectDetails.lastAccessedFileId}
                  ideProjectId={ideProjectId}
                  ideProjectDetails={ideProjectDetails}
                  nodeSelected={nodeSelected}
                  handleNewFile={handleNewFile}
                  handleFileDeleted={handleFileDeleted}
                  handleFileRenamed={handleFileRenamed}
                  readOnlyMode={readOnlyMode}
                  projectStack="html_css"
                />
              </div>
            ) : (
              false
            )}

            {fileLoadingInProgress && (
              <div className="col-10 text-center mt-3">
                <Skeleton active="true" paragraph={{ rows: 8 }} />
              </div>
            )}

            {selectedFile ? (
              <div className={getCssClassName("code-editor-output-container")}>
                <div className="row mb-1">
                  <div className={getCssClassName("code-editor")}>
                    <div className="row mb-1">
                      <div className="col">
                        <Space direction="vertical">
                          <Space wrap>
                          {isMobile ?(
                            <OptionIcon
                            onClick={() => openMobileFileManagerModal()}
                            style={{ cursor: 'pointer' }}
                          />
                          ): fileManagerExpanded ? (
                            <ExpandIcon
                              onClick={() => setFileManagerExpanded(false)}
                              style={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <CollapseIcon
                              onClick={() => setFileManagerExpanded(true)}
                              style={{ cursor: 'pointer' }}
                            />
                          )}

                            <span className="badge" style={{backgroundColor:'#404040'}}>
                              {selectedFile.fileName}
                            </span>

                            {unsavedChangePresent && (
                              <Tooltip
                                placement="topLeft"
                                title="There are unsaved changes in your editor"
                              >
                                <Badge status="error" />
                              </Tooltip>
                            )}
                          </Space>
                        </Space>

                        {selectedFile.fileMimeType !== "image" &&
                        !fileLoadingInProgress ? (
                          <Space direction="vertical" className="float-end">
                            <Space wrap>
                              {!readOnlyMode && (
                                <Button
                                  loading={fileSavingInProgress}
                                  style={{backgroundColor:'#4235A1'}}
                                  className="float-end"
                                  type="primary"
                                  onClick={handleSaveFile}
                                  size="small"
                                >
                                  Save
                                </Button>
                              )}
                              <Button
                                style={{backgroundColor:'#4235A1'}}
                                className="float-end"
                                type="primary"
                                onClick={handleOpen}
                                size="small"
                              >
                                Open
                              </Button>
                              {/* <Button
                                className="float-end"
                                type="primary"
                                onClick={handleRunCode}
                                size="small"
                              >
                                Run
                              </Button> */}
                              <Button className="float-end"
                                style={{backgroundColor:'#FFC200'}}
                                type="primary"
                                size="small"
                                onClick={handleSelectedTheme}>
                                {selectedTheme === 'light' ? <BulbIcon style={{fontSize:'16px'}}/> : <BulbIcon />}
                              </Button>

                              {/* {outputWindowExpanded ? (
                                <FullscreenExitOutlined
                                  onClick={toggleOutputWindow}
                                  style={{ fontSize: "1.5rem" }}
                                />
                              ) : (
                                <FullscreenOutlined
                                  onClick={toggleOutputWindow}
                                  style={{ fontSize: "1.5rem" }}
                                />
                              )} */}
                            </Space>
                          </Space>
                        ) : (
                          false
                        )}
                      </div>
                    </div>

                    <div className="row mb-1">
                      {selectedFile.fileMimeType === "image" || selectedFile.fileMimeType  === "unknown"? (
                        <div className="col text-center">
                          <img
                            alt={selectedFile.fileName}
                            className="img-thumbnail img-fluid"
                            src={selectedFile.fileS3reference}
                          ></img>
                        </div>
                      ) : (
                        <div className="col p-0" style={{overflow: "auto"}}>
                          <Editor
                            height={isMobile ? '82vh' : '80vh'}
                            onChange={handleCodeChange}
                            defaultLanguage={
                              selectedFile.fileMimeType
                                ? selectedFile.fileMimeType
                                : "html"
                            }
                            theme={selectedTheme}
                            // defaultValue="# Enter you code here..."
                            value={
                              selectedFile.fileContent
                                ? selectedFile.fileContent
                                : ""
                            }
                            options={{ readOnly: readOnlyMode }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={getCssClassName("output-window")}>
                    <div className="row">
                      <div className="col p-0 fw-medium">Output</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              false
            )}
          </div>
        </div>
        <Modal
          open={mobileFileManagerModal}
          onCancel={closeMobileFileManagerModal}
          footer={null}
          width={'60%'}
          bodyStyle={{ height: '90vh', overflow: 'auto', padding: '10px' }}
          style={{marginLeft:'0px',marginTop:'-90px' }}
          >
          {ideProjectDetails && (
            <FileManager
            fileStructure={
              ideProjectDetails.fileMetaList
                ? ideProjectDetails.fileMetaList
                : []
            }
            defaultSelectedKey={ideProjectDetails.lastAccessedFileId}
            ideProjectId={ideProjectId}
            ideProjectDetails={ideProjectDetails}
            nodeSelected={nodeSelected}
            handleNewFile={handleNewFile}
            handleFileDeleted={handleFileDeleted}
            handleFileRenamed={handleFileRenamed}
            readOnlyMode={readOnlyMode}
            projectStack="html_css"
          />
          )}
        </Modal>
        {newProjectModal && <CreateIDEProjectModal projectStack="html_css" />}
      </div>
    </div>
  );
};

export default HtmlCssIDE;
